/**
 * SGR Fee Management System
 * Improved approach: Better event handling, cart refresh, and error handling
 */

class SGRFeeManager {
  constructor() {
    this.syncing = false;
    this.syncQueue = Promise.resolve();
    this.lastCartState = null;
    this.debounceTimer = null;
  }

  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return await response.json();
  }

  calculateRequiredFees(cart) {
    const required = new Map();

    for (const item of cart.items) {
      if (item.properties?._is_sgr_fee === 'true') continue;

      const feeVariantId = item.properties?._sgr_fee_variant_id;
      const unitsPerItem = parseInt(item.properties?._sgr_units_per_item || '1', 10);

      if (feeVariantId) {
        const totalFeeUnits = item.quantity * unitsPerItem;
        required.set(String(feeVariantId), (required.get(String(feeVariantId)) || 0) + totalFeeUnits);
      }
    }

    return required;
  }

  getCurrentFees(cart) {
    const current = new Map();

    for (const item of cart.items) {
      if (item.properties?._is_sgr_fee === 'true') {
        const variantKey = String(item.variant_id);
        const existing = current.get(variantKey) || [];
        existing.push(item);
        current.set(variantKey, existing);
      }
    }

    return current;
  }

  getCartHash(cart) {
    // Create a hash of non-fee items to detect changes
    return cart.items
      .filter(item => item.properties?._is_sgr_fee !== 'true')
      .map(item => `${item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  async batchUpdateCart(updates) {
    // Batch all cart updates into a single request
    if (updates.length === 0) return null;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: Object.fromEntries(updates) })
    });

    if (!response.ok) throw new Error(`Batch update failed: ${response.status}`);
    return await response.json();
  }

  async addFeeProduct(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        properties: { _is_sgr_fee: 'true' },
      }),
    });
    if (!response.ok) throw new Error(`Add failed: ${response.status}`);
    return await response.json();
  }

  getVisibleItemCount(cart) {
    // Count only non-SGR fee items
    return cart.items
      .filter(item => item.properties?._is_sgr_fee !== 'true')
      .reduce((total, item) => total + item.quantity, 0);
  }

  async refreshCart() {
    // Trigger the official cart changed event with updated cart data
    try {
      // Mark that this change is triggered by SGR to prevent infinite loops
      if (window.SGRFee && window.SGRFee.markAsTriggered) {
        window.SGRFee.markAsTriggered();
      }
      
      const updatedCart = await this.getCart();
      
      // Update item_count to exclude SGR fee items for cart bubble
      updatedCart.item_count = this.getVisibleItemCount(updatedCart);
      
      // Dispatch the official cart:changed event
      document.dispatchEvent(new CustomEvent('cart:changed', {
        detail: { cartData: updatedCart },
        bubbles: true
      }));
      
      console.log('[SGR] Cart refresh event dispatched');
    } catch (error) {
      console.error('[SGR] Cart refresh error:', error);
    }
  }

  async syncInternal() {
    console.log('[SGR] Starting sync...');

    try {
      // SGR fees only apply to Romania
      if (window.__SGR_COUNTRY__ !== 'RO') {
        console.log('[SGR] Country is not RO, removing any existing fees...');
        const cart = await this.getCart();
        const currentFees = this.getCurrentFees(cart);
        const removeBatch = [];
        for (const [, feeItems] of currentFees.entries()) {
          for (const item of feeItems) {
            removeBatch.push([item.key, 0]);
          }
        }
        if (removeBatch.length > 0) {
          await this.batchUpdateCart(removeBatch);
          this.lastCartState = null;
          this.refreshCart();
          return { changed: true, success: true };
        }
        return { changed: false, success: true };
      }

      const cart = await this.getCart();
      const cartHash = this.getCartHash(cart);
      
      // Skip if cart hasn't changed
      if (this.lastCartState === cartHash) {
        console.log('[SGR] Cart unchanged, skipping sync');
        return { changed: false, success: true };
      }

      const requiredFees = this.calculateRequiredFees(cart);
      const currentFees = this.getCurrentFees(cart);

      console.log('[SGR] Required fees:', Object.fromEntries(requiredFees));
      console.log('[SGR] Current fees:', Array.from(currentFees.keys()));

      let hasChanges = false;
      const updateBatch = [];

      // Process required fees
      for (const [variantId, requiredQty] of requiredFees.entries()) {
        const feeItems = currentFees.get(variantId) || [];
        const currentTotal = feeItems.reduce((sum, item) => sum + item.quantity, 0);

        if (currentTotal === 0 && requiredQty > 0) {
          console.log(`[SGR] Adding fee ${variantId} qty ${requiredQty}`);
          await this.addFeeProduct(variantId, requiredQty);
          hasChanges = true;
        } else if (currentTotal !== requiredQty) {
          console.log(`[SGR] Updating fee ${variantId} from ${currentTotal} to ${requiredQty}`);
          // Keep first item, update its quantity
          if (feeItems.length > 0) {
            updateBatch.push([feeItems[0].key, requiredQty]);
            // Remove duplicates
            for (let i = 1; i < feeItems.length; i++) {
              updateBatch.push([feeItems[i].key, 0]);
            }
            hasChanges = true;
          }
        }
        
        currentFees.delete(variantId);
      }

      // Remove orphaned fees
      for (const [variantId, feeItems] of currentFees.entries()) {
        console.log(`[SGR] Removing orphaned fee ${variantId}`);
        for (const item of feeItems) {
          updateBatch.push([item.key, 0]);
        }
        hasChanges = true;
      }

      // Execute batch updates
      if (updateBatch.length > 0) {
        await this.batchUpdateCart(updateBatch);
      }

      if (hasChanges) {
        console.log('[SGR] Sync complete with changes');
        this.lastCartState = null; // Force recheck on next sync
        this.refreshCart();
      } else {
        console.log('[SGR] Sync complete, no changes needed');
        this.lastCartState = cartHash;
      }

      return { changed: hasChanges, success: true };
    } catch (error) {
      console.error('[SGR] Sync error:', error);
      return { changed: false, success: false, error };
    }
  }

  async sync(force = false) {
    // Queue syncs to prevent concurrent execution
    this.syncQueue = this.syncQueue.then(async () => {
      if (this.syncing && !force) {
        console.log('[SGR] Sync already in progress, skipping');
        return { changed: false, success: true, skipped: true };
      }

      this.syncing = true;
      try {
        return await this.syncInternal();
      } finally {
        this.syncing = false;
      }
    });

    return this.syncQueue;
  }

  debouncedSync(delay = 300) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.sync().catch(console.error);
    }, delay);
  }
}

const sgrManager = new SGRFeeManager();

// Global API
window.SGRFee = {
  sync: (force = false) => sgrManager.sync(force),
  debouncedSync: (delay) => sgrManager.debouncedSync(delay),
};

// Initialize event listeners
if (typeof document !== 'undefined') {
  let isSGRTriggered = false;

  // Listen to cart changes to sync fees (but not when we're the ones changing it)
  document.addEventListener('cart:changed', (event) => {
    if (isSGRTriggered) {
      console.log('[SGR] Skipping sync - triggered by SGR itself');
      isSGRTriggered = false;
      return;
    }
    
    console.log('[SGR] Cart changed event detected');
    sgrManager.debouncedSync(400);
  });

  // When cart drawer opens, do a quick sync check
  window.addEventListener('fluco:modal:cartDrawer:opening', () => {
    console.log('[SGR] Cart drawer opening, checking fees');
    sgrManager.debouncedSync(200);
  });

  // Store reference to detect SGR-triggered changes
  window.SGRFee.markAsTriggered = () => {
    isSGRTriggered = true;
  };

  console.log('[SGR] Fee manager initialized');
}
